/**
 * Order Service Hooks
 *
 * React Query hooks for order operations
 * Baseado nas funções disponíveis no orderService.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderService, OrderService } from '../services/api';
import { ORDER_KEYS } from './keys';
import { wsLog } from '@utils/wsLogger';
import type {
  Order,
  TodayOrdersParams,
  TodayOrdersResponse,
  OrderSummaryParams,
  OrderSummaryResponse,
  OrderSummaryAllParams,
  OrderSummaryAllResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  OrderDetailParams,
  OrderDetailResponse,
  OrderStats,
  KitchenDashboard,
  BatchOrderResult,
  IntervalResponse,
} from '../services/api/orderTypes';
import { OrderStatus } from '../services/api/orderTypes';

// ==================== ORDER QUERIES ====================

/**
 * Hook para obter pedidos de hoje (single load, no pooling)
 * Use this for initial data loading only - for real-time updates, use useWebSocketOrders
 */
export function useTodayOrders(params: TodayOrdersParams, options?: { enabled?: boolean; staleTime?: number }) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, params],
    queryFn: async () => {
      wsLog.debug('🚀 HTTP Request - Getting today orders:', params);
      const startTime = Date.now();

      try {
        const result = await orderService.getTodayOrders(params);
        const endTime = Date.now();

        // O fallback de resposta nula saía daqui com outra forma
        // ({page, limit, hasNext, hasPrev} em vez de {summary, pagination}),
        // e a união dos dois retornos colapsava para `never` em quem consumia
        // o hook. getTodayOrders já normaliza null para
        // {orders: [], total: 0, summary}, então a checagem virou redundante.

        wsLog.debug('✅ HTTP Response - Today orders received:', {
          params,
          responseTime: `${endTime - startTime}ms`,
          ordersCount: result.orders?.length || 0,
          orders: result.orders?.map((order) => ({
            id: order.id,
            order_number: order.order_number,
            student_name: order.student_name,
            class_name: order.student_class,
            status: order.status,
            productsCount: order.items?.length || 0,
            created_at: order.created_at,
          })),
        });

        return result;
      } catch (error) {
        wsLog.error('❌ HTTP Error - Failed to get today orders:', {
          params,
          error: error instanceof Error ? error.message : error,
          responseTime: `${Date.now() - startTime}ms`,
        });
        throw error;
      }
    },
    enabled: !!params.establishment_id && options?.enabled !== false,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutos default (não atualiza automaticamente)
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
  });
}

/**
 * Hook para obter resumo de pedidos
 */
export function useOrderSummary(params: OrderSummaryParams) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS, params],
    queryFn: () => orderService.getOrderSummary(params),
    enabled: !!params.establishment_id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para obter resumo completo de pedidos
 */
export function useOrderSummaryAll(params: OrderSummaryAllParams) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS, params],
    queryFn: () => orderService.getOrderSummaryAll(params),
    enabled: !!params.establishment_id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para obter detalhes de um pedido
 */
export function useOrderDetail(params: OrderDetailParams) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER, params.id, params],
    queryFn: () => orderService.getOrderDetail(params),
    enabled: !!params.id,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
}

/**
 * Hook para obter pedido por ID (simplificado)
 */
export function useOrderById(orderId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER, orderId],
    queryFn: () => orderService.getOrderById(orderId),
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
}

/**
 * Hook para verificar se pedido existe
 */
export function useOrderExists(orderId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER, orderId],
    queryFn: () => orderService.orderExists(orderId),
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para obter pedidos por status
 */
export function useOrdersByStatus(establishmentId: string, status: OrderStatus) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, status }],
    queryFn: () => orderService.getOrdersByStatus(establishmentId, status),
    enabled: !!establishmentId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter pedidos pendentes
 */
export function usePendingOrders(establishmentId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, status: OrderStatus.PENDING }],
    queryFn: () => orderService.getPendingOrders(establishmentId),
    enabled: !!establishmentId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter pedidos em preparo
 */
export function useOrdersInPreparation(establishmentId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, status: OrderStatus.IN_PREPARATION }],
    queryFn: () => orderService.getOrdersInPreparation(establishmentId),
    enabled: !!establishmentId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter pedidos prontos
 */
export function useReadyOrders(establishmentId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, status: OrderStatus.READY }],
    queryFn: () => orderService.getReadyOrders(establishmentId),
    enabled: !!establishmentId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter pedidos entregues
 */
export function useDeliveredOrders(establishmentId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, status: OrderStatus.DELIVERED }],
    queryFn: () => orderService.getDeliveredOrders(establishmentId),
    enabled: !!establishmentId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter pedidos de um estudante
 */
export function useStudentOrders(establishmentId: string, studentId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, student_id: studentId }],
    queryFn: () => orderService.getStudentOrders(establishmentId, studentId),
    enabled: !!establishmentId && !!studentId,
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
}

/**
 * Hook para obter dashboard da cozinha
 */
export function useKitchenDashboard(establishmentId: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS, { establishment_id: establishmentId, type: 'kitchen-dashboard' }],
    queryFn: () => orderService.getKitchenDashboard(establishmentId),
    enabled: !!establishmentId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter estatísticas de pedidos
 */
export function useOrderStats(establishmentId: string, dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS, { establishment_id: establishmentId, dateFrom, dateTo }],
    queryFn: () => orderService.getOrderStats(establishmentId, dateFrom, dateTo),
    enabled: !!establishmentId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// ==================== ORDER MUTATIONS ====================

/**
 * Hook para atualizar status do pedido
 */
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.UPDATE_ORDER],
    mutationFn: ({ orderId, data }: { orderId: string; data: UpdateOrderStatusRequest }) => orderService.updateOrderStatus(orderId, data),
    onSuccess: (data, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, variables.orderId] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao atualizar status do pedido:', error);
    },
  });
}

/**
 * Hook para iniciar preparo do pedido
 */
export function useStartPreparation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.PROCESS_ORDER],
    mutationFn: (orderId: string) => orderService.startPreparation(orderId),
    onSuccess: (data, orderId) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, orderId] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao iniciar preparo do pedido:', error);
    },
  });
}

/**
 * Hook para marcar pedido como pronto
 */
export function useMarkOrderReady() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.PROCESS_ORDER],
    mutationFn: (orderId: string) => orderService.markOrderReady(orderId),
    onSuccess: (data, orderId) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, orderId] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao marcar pedido como pronto:', error);
    },
  });
}

/**
 * Hook para marcar pedido como entregue
 */
export function useMarkOrderDelivered() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.PROCESS_ORDER],
    mutationFn: ({ orderId, deliveryTime }: { orderId: string; deliveryTime?: string }) => orderService.markOrderDelivered(orderId, deliveryTime),
    onSuccess: (data, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, variables.orderId] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao marcar pedido como entregue:', error);
    },
  });
}

/**
 * Hook para cancelar pedido
 */
export function useCancelOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.CANCEL_ORDER],
    mutationFn: ({ orderId, reason }: { orderId: string; reason?: string }) => orderService.cancelOrder(orderId, reason),
    onSuccess: (data, variables) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, variables.orderId] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao cancelar pedido:', error);
    },
  });
}

/**
 * Hook para atualizar status de múltiplos pedidos
 */
export function useBatchUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.UPDATE_ORDER],
    mutationFn: ({ orderIds, status, notes }: { orderIds: string[]; status: OrderStatus; notes?: string }) =>
      orderService.batchUpdateStatus(orderIds, status, notes),
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });

      // Invalidar queries específicas dos pedidos atualizados
      data.results.forEach((order) => {
        queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, order.id] });
      });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao atualizar múltiplos pedidos:', error);
    },
  });
}

/**
 * Hook para cancelar múltiplos pedidos
 */
export function useBatchCancelOrders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ORDER_KEYS.CANCEL_ORDER],
    mutationFn: ({ orderIds, reason }: { orderIds: string[]; reason?: string }) => orderService.batchCancelOrders(orderIds, reason),
    onSuccess: (data) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });

      // Invalidar queries específicas dos pedidos cancelados
      data.results.forEach((order) => {
        queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER, order.id] });
      });
    },
    onError: (error: Error) => {
      wsLog.error('Erro ao cancelar múltiplos pedidos:', error);
    },
  });
}

// ==================== UTILITY HOOKS ====================

/**
 * Hook para obter opções de status
 */
export function useOrderStatusOptions() {
  return useQuery({
    queryKey: ['order:status-options'],
    queryFn: () => OrderService.getStatusOptions(),
    staleTime: Infinity, // Nunca expira
  });
}

/**
 * Hook para calcular total do pedido
 */
export function useCalculateOrderTotal(items: any[]) {
  return useQuery({
    queryKey: ['order:calculate-total', items],
    queryFn: () => OrderService.calculateOrderTotal(items),
    enabled: items.length > 0,
    staleTime: Infinity, // Nunca expira
  });
}

/**
 * Hook para formatar número do pedido
 */
export function useFormatOrderNumber(orderNumber: string) {
  return useQuery({
    queryKey: ['order:format-number', orderNumber],
    queryFn: () => OrderService.formatOrderNumber(orderNumber),
    enabled: !!orderNumber,
    staleTime: Infinity, // Nunca expira
  });
}

/**
 * Hook para obter prioridade do pedido
 */
export function useOrderPriority(order: Order) {
  return useQuery({
    queryKey: ['order:priority', order.id],
    queryFn: () => OrderService.getOrderPriority(order),
    enabled: !!order,
    staleTime: 30 * 1000, // 30 segundos
  });
}

/**
 * Hook para obter intervalos ativos de um estabelecimento
 */
export function useCurrentIntervals(establishmentId: string): any {
  return useQuery({
    queryKey: [ORDER_KEYS.LIST_ORDERS, { establishment_id: establishmentId, type: 'intervals' }],
    queryFn: () => orderService.getCurrentIntervals(establishmentId),
    enabled: !!establishmentId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// Export WebSocket hooks
// useTransformedOrders foi removido: era código morto e montava a queryKey a
// partir da concatenação de todos os ids de pedido, criando uma entrada nova no
// cache do React Query a cada mudança na lista.
export { useWebSocketOrders, useInitialOrders } from './useWebSocketOrders';
export type { UseWebSocketOrdersParams } from './useWebSocketOrders';
